import "../Home/Styles.css";
import LegalPageLinks from "../../components/LegalPageLinks";
import { CHURCH_INFO } from "../../utils/churchInfo";
import { usePageMeta } from "../../utils/usePageMeta";

const rightsBlocks = [
  {
    title: "Church-Owned Content",
    body:
      "Unless otherwise stated, sermons, sermon notes, videos, graphics, photos, designs, logos, app content, and website content are owned by Trinity Christian Church or used with permission.",
    tone: "bg-tcc-blue/10 text-tcc-blue",
    dot: "bg-tcc-blue",
  },
  {
    title: "Personal Use",
    body:
      "You may view, listen to, and share official church content for personal, devotional, ministry, or non-commercial use by linking to the official source.",
    tone: "bg-tcc-green/15 text-[#5f9024]",
    dot: "bg-tcc-green",
  },
  {
    title: "Permission Required",
    body:
      "You may not republish, sell, modify, rehost, or commercially distribute church content, branding, or media without prior written permission.",
    tone: "bg-tcc-brown/10 text-tcc-brown",
    dot: "bg-tcc-brown",
  },
];

const CopyrightPage = () => {
  usePageMeta(
    "Trinity Christian Church Copyright",
    "Read the Trinity Christian Church copyright and content use information for sermons, notes, videos, branding, website content, and app media."
  );

  return (
    <main className="min-h-screen bg-[#f4f1ec] text-slate-900">
      <section className="relative overflow-hidden bg-[#141414]">
        <div className="absolute inset-0 grid grid-cols-1 md:grid-cols-3">
          <div className="bg-tcc-blue/35" />
          <div className="bg-tcc-green/25" />
          <div className="bg-tcc-brown/35" />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.15),_transparent_38%)]" />
        <div className="relative mx-auto max-w-6xl px-6 py-20 md:px-10 md:py-24">
          <a
            href="/"
            className="inline-flex items-center rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm font-medium text-white/85 backdrop-blur-sm transition hover:border-white/40 hover:bg-white/10 hover:text-white"
          >
            Back to Home
          </a>
          <div className="mt-10 max-w-4xl">
            <p className="text-sm uppercase tracking-[0.45em] text-white/70">
              Trinity Christian Church
            </p>
            <h1 className="mt-4 text-4xl text-white md:text-6xl">
              <span className="archivo-black-regular">Copyright</span>{" "}
              <i className="anton-regular-italic text-tcc-green">and Media Use</i>
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-white/85">
              Guidance for using church sermons, videos, sermon notes, logos,
              graphics, app content, and website materials.
            </p>
            <LegalPageLinks current="copyright" />
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto -mt-8 max-w-6xl px-6 pb-16 md:px-10 md:pb-24">
        <div className="grid gap-4 md:grid-cols-3">
          {rightsBlocks.map((block) => (
            <div
              key={block.title}
              className="rounded-[2rem] border border-black/5 bg-white p-6 shadow-[0_18px_55px_rgba(0,0,0,0.08)]"
            >
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] ${block.tone}`}
              >
                Copyright Summary
              </span>
              <h2 className="mt-4 text-2xl font-semibold text-slate-900">
                {block.title}
              </h2>
              <p className="mt-4 text-base leading-8 text-slate-700">
                {block.body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <section className="rounded-[2rem] border border-black/5 bg-white p-8 shadow-[0_18px_55px_rgba(0,0,0,0.08)]">
            <span className="inline-flex rounded-full bg-tcc-blue/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-tcc-blue">
              What You May Do
            </span>
            <h2 className="mt-4 text-3xl text-tcc-blue archivo-black-regular">
              Approved use of church content
            </h2>
            <ul className="mt-6 space-y-3 text-base leading-8 text-slate-700">
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-blue" />
                <span>Read, watch, or listen to official church content for personal use.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-blue" />
                <span>Share official links to sermons, events, notes, or app pages.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-blue" />
                <span>Reference church content for ministry or devotional purposes without altering ownership notices.</span>
              </li>
            </ul>
          </section>

          <section className="rounded-[2rem] border border-black/5 bg-white p-8 shadow-[0_18px_55px_rgba(0,0,0,0.08)]">
            <span className="inline-flex rounded-full bg-tcc-brown/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-tcc-brown">
              What Requires Permission
            </span>
            <h2 className="mt-4 text-3xl text-tcc-brown archivo-black-regular">
              Uses not allowed without approval
            </h2>
            <ul className="mt-6 space-y-3 text-base leading-8 text-slate-700">
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-brown" />
                <span>Uploading full sermons, videos, or sermon notes to another platform as your own hosted copy.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-brown" />
                <span>Using the church name, logo, graphics, or visual identity in advertising, merchandise, or branding.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-3 h-2.5 w-2.5 shrink-0 rounded-full bg-tcc-brown" />
                <span>Editing, reselling, repackaging, or commercially distributing church-owned content.</span>
              </li>
            </ul>
          </section>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          <section className="rounded-[2rem] border border-black/5 bg-white p-8 shadow-[0_18px_55px_rgba(0,0,0,0.08)]">
            <span className="inline-flex rounded-full bg-tcc-green/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-[#5f9024]">
              Third-Party Material
            </span>
            <h2 className="mt-4 text-3xl text-[#5f9024] archivo-black-regular">
              Third-party and licensed content
            </h2>
            <p className="mt-4 text-base leading-8 text-slate-700">
              Some materials linked through the website or mobile app may belong
              to third parties, including payment providers, video platforms,
              social networks, or embedded media services. Those materials remain
              subject to the rights of their respective owners.
            </p>
            <p className="mt-4 text-base leading-8 text-slate-700">
              If you are unsure whether something is church-owned or third-party
              content, contact the church before reusing it.
            </p>
          </section>

          <section className="overflow-hidden rounded-[2rem] shadow-[0_24px_80px_rgba(6,76,124,0.18)]">
            <div className="bg-gradient-to-r from-tcc-blue via-tcc-green to-tcc-brown p-8">
              <span className="inline-flex rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-white">
                Permissions and Reports
              </span>
              <h2 className="mt-4 text-3xl text-white archivo-black-regular">
                Need permission or need to report an issue?
              </h2>
              <p className="mt-4 text-base leading-8 text-white/90">
                Contact Trinity Christian Church for permission requests,
                copyright questions, or concerns about unauthorized use.
              </p>
            </div>
            <div className="bg-white p-8">
              <div className="space-y-5 text-slate-800">
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-slate-500">
                    Email
                  </p>
                  <a
                    href={`mailto:${CHURCH_INFO.email}`}
                    className="mt-2 block text-xl font-semibold text-tcc-blue"
                  >
                    {CHURCH_INFO.email}
                  </a>
                </div>
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-slate-500">
                    Phone
                  </p>
                  <a
                    href={CHURCH_INFO.phoneHref}
                    className="mt-2 block text-xl font-semibold text-tcc-blue"
                  >
                    {CHURCH_INFO.phoneDisplay}
                  </a>
                </div>
                <div>
                  <p className="text-sm uppercase tracking-[0.2em] text-slate-500">
                    Address
                  </p>
                  <p className="mt-2 text-lg font-semibold">
                    {CHURCH_INFO.addressLine1}
                  </p>
                  <p className="text-slate-600">{CHURCH_INFO.addressLine2}</p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-8 rounded-[2rem] border border-black/5 bg-white p-8 shadow-[0_18px_55px_rgba(0,0,0,0.08)]">
          <span className="inline-flex rounded-full bg-tcc-blue/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-tcc-blue">
            Related Pages
          </span>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <a
              href="/support"
              className="rounded-3xl bg-[#f7f9fb] px-5 py-4 font-semibold text-tcc-blue"
            >
              App Support
            </a>
            <a
              href="/privacy"
              className="rounded-3xl bg-[#f7f9fb] px-5 py-4 font-semibold text-tcc-blue"
            >
              Privacy Policy
            </a>
            <a
              href={CHURCH_INFO.websiteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-3xl bg-[#f7f9fb] px-5 py-4 font-semibold text-tcc-blue"
            >
              {CHURCH_INFO.websiteLabel}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CopyrightPage;
